// LyraPromptOptimizer.jsx
import React, { useState, useEffect } from "react";
import api from "../api";
import Card from "./ui/Card";
import CardContent from "./ui/CardContent";
import Input from "./ui/Input";
import Button from "./ui/Button";
import Textarea from "./ui/Textarea";
import StatsDashboard from "./StatsDashboard";
import TemplateLibrary from "./TemplateLibrary";

const recommendations = {
  ChatGPT:
    "Great for general use, structured tasks, and strong reasoning. Optimized via OpenAI.",
  Claude:
    "Ideal for long context, reasoning chains, and clarity-heavy tasks. Optimized via Anthropic.",
};

const welcomeMessage =
`Hello! I'm Lyra. Please select the target AI, set the BASIC or DETAIL level, and share your prompt.`;

export default function LyraPromptOptimizer() {
  const [input, setInput] = useState("");
  const [targetAI, setTargetAI] = useState("ChatGPT");
  const [mode, setMode] = useState("DETAIL");
  const [surface, setSurface] = useState("chat");
  const [optimizedPrompt, setOptimizedPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [expandedIdx, setExpandedIdx] = useState(null);

  // Adaptive clarifying interview state. The flow is a short, dependent
  // back-and-forth: 1-2 questions per round, the model decides when it's
  // "ready", with a hard cap and an always-available "Optimize now" escape.
  const [clarifyActive, setClarifyActive] = useState(false); // panel shown?
  const [clarifyLoading, setClarifyLoading] = useState(false);
  const [clarifyRound, setClarifyRound] = useState(0); // round currently shown
  const [maxRounds, setMaxRounds] = useState(3);
  const [clarifyReady, setClarifyReady] = useState(false);
  const [clarifyReason, setClarifyReason] = useState("");
  const [clarifyTrack, setClarifyTrack] = useState(null); // code surface: audit|build|both
  const [qaHistory, setQaHistory] = useState([]); // [{q,a}] from completed rounds
  const [currentQuestions, setCurrentQuestions] = useState([]); // [{id,text,hint,options}]
  const [currentAnswers, setCurrentAnswers] = useState({}); // {id: value}

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const res = await api.get("/prompts");
        setHistory(res.data);
      } catch (err) {
        console.error("Error fetching prompt history:", err);
      }
    };
    fetchHistory();
  }, []);

  const savePromptToBackend = async (raw, optimized, modeUsed, targetAI) => {
    try {
      const res = await api.post("/prompts", {
        raw,
        optimized,
        mode: modeUsed,
        targetAI,
      });
      setHistory((prev) => [res.data, ...prev]);
    } catch (error) {
      console.error("Error saving prompt:", error);
    }
  };

  // Reset the clarifying interview (e.g. before starting a fresh one).
  const resetClarify = () => {
    setClarifyActive(false);
    setClarifyRound(0);
    setClarifyReady(false);
    setClarifyReason("");
    setClarifyTrack(null);
    setQaHistory([]);
    setCurrentQuestions([]);
    setCurrentAnswers({});
  };

  // Fetch one round of the adaptive interview. `priorHistory` is the full
  // accumulated Q/A; `round` is the round being requested (1-based).
  const fetchClarifyRound = async (priorHistory, round) => {
    if (!input) return;
    setClarifyLoading(true);
    setClarifyActive(true);
    try {
      const res = await api.post("/lyra-optimize/clarify", {
        prompt: input,
        surface,
        targetAI,
        history: priorHistory,
        round,
      });
      const data = res.data || {};
      setClarifyRound(data.round || round);
      setMaxRounds(data.maxRounds || 3);
      setClarifyReason(data.reason || "");
      if (data.track) setClarifyTrack(data.track);

      const questions = Array.isArray(data.questions) ? data.questions : [];
      if (data.ready || questions.length === 0) {
        setClarifyReady(true);
        setCurrentQuestions([]);
        setCurrentAnswers({});
      } else {
        setClarifyReady(false);
        setCurrentQuestions(questions);
        setCurrentAnswers({});
      }
    } catch (err) {
      console.error("Error fetching clarifying questions:", err);
      // Non-blocking: let the user optimize anyway.
      setClarifyReady(true);
      setCurrentQuestions([]);
      setClarifyReason("Couldn't fetch questions — you can optimize now.");
    } finally {
      setClarifyLoading(false);
    }
  };

  // Start a fresh interview at round 1.
  const startClarify = () => {
    if (!input) return;
    setQaHistory([]);
    setClarifyTrack(null);
    setClarifyReady(false);
    fetchClarifyRound([], 1);
  };

  // Submit this round's answers and fetch the next round.
  const continueClarify = () => {
    const answered = currentQuestions.map((q) => ({
      q: q.text,
      a: currentAnswers[q.id] || "",
    }));
    const newHistory = [...qaHistory, ...answered];
    setQaHistory(newHistory);
    fetchClarifyRound(newHistory, clarifyRound + 1);
  };

  const handleAnswerChange = (id, value) => {
    setCurrentAnswers((prev) => ({ ...prev, [id]: value }));
  };

  // All answered Q/A so far: completed rounds plus any answers typed into the
  // current (not-yet-submitted) round, so "Optimize now" never drops context.
  const mergeClarifications = () => {
    const pairs = [...qaHistory];
    currentQuestions.forEach((q) => {
      const a = currentAnswers[q.id];
      if (a && a.trim()) pairs.push({ q: q.text, a });
    });
    return pairs
      .filter((p) => p.a && p.a.trim())
      .map((p) => `Q: ${p.q}\nA: ${p.a}`)
      .join("\n");
  };

  const handleCopy = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        alert("Prompt copied to clipboard!");
      })
      .catch((err) => console.error("Clipboard copy failed:", err));
  };

  const optimizePrompt = async (overrideInput = null) => {
    const baseInput = overrideInput !== null ? overrideInput : input;
    if (!baseInput) return;
    setLoading(true);
    setOptimizedPrompt("");
    try {
      let finalPrompt = baseInput;
      // Only fold clarifications into the live prompt — not when re-optimizing
      // a past history entry (which passes overrideInput).
      const clarifications =
        overrideInput === null && mode === "DETAIL" ? mergeClarifications() : "";
      if (clarifications) finalPrompt += "\n\n" + clarifications;

      // Carry the audit/build track to the optimizer for the code surface so
      // the brief is shaped correctly (build brief vs read-only audit).
      const track =
        overrideInput === null && surface === "code" ? clarifyTrack : null;

      // Stream the optimized prompt so it renders token-by-token. axios buffers
      // the whole response, so use fetch + a stream reader here. The token is
      // read at call time (consistent with the shared api client).
      const token = localStorage.getItem("lyraToken");
      const res = await fetch(`${api.defaults.baseURL}/lyra-optimize/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ prompt: finalPrompt, mode, targetAI, surface, track }),
      });

      if (!res.ok || !res.body) {
        throw new Error(`Request failed with status ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let optimized = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        optimized += chunk;
        setOptimizedPrompt((prev) => prev + chunk);
      }

      if (optimized.trim()) {
        savePromptToBackend(finalPrompt, optimized, mode, targetAI);
      }
    } catch (err) {
      console.error("Optimization failed:", err);
      setOptimizedPrompt(
        "Error: Failed to generate optimized prompt. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const showClarifyPanel = mode === "DETAIL" && clarifyActive;

  return (
    <div className="max-w-3xl mx-auto p-4 space-y-4 font-sans">

      <Card>
        <CardContent>
          <div className="whitespace-pre-wrap text-sm">{welcomeMessage}</div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="font-semibold">Target AI:</label>
          <select
            value={targetAI}
            onChange={(e) => setTargetAI(e.target.value)}
            className="w-full border p-2 rounded"
          >
            <option>ChatGPT</option>
            <option>Claude</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            {recommendations[targetAI]}
          </p>
        </div>
        <div>
          <label className="font-semibold">Prompt Style:</label>
          <select
            value={mode}
            onChange={(e) => {
              setMode(e.target.value);
              // BASIC has no clarify flow; don't leave a stale session to
              // reappear when switching back to DETAIL.
              resetClarify();
            }}
            className="w-full border p-2 rounded"
          >
            <option>DETAIL</option>
            <option>BASIC</option>
          </select>
        </div>
        <div>
          <label className="font-semibold">Surface:</label>
          <select
            value={surface}
            onChange={(e) => {
              setSurface(e.target.value);
              // Track and questions are surface-specific; start clean.
              resetClarify();
            }}
            className="w-full border p-2 rounded"
          >
            <option value="chat">Chat</option>
            <option value="code">Code (agent)</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            {surface === "code"
              ? "For a repo-aware coding agent: it investigates the codebase itself instead of asking."
              : "For a chat assistant: the prompt is self-contained."}
          </p>
        </div>
      </div>

      <TemplateLibrary onUse={setInput} currentInput={input} />

      <Textarea
        placeholder="Enter your rough prompt here..."
        value={input}
        onChange={(e) => {
          setInput(e.target.value);
          // Editing the prompt invalidates an in-progress clarify session
          // (its Q/A were about the previous prompt), so start fresh.
          if (clarifyActive) resetClarify();
        }}
        rows={4}
      />

      {mode === "DETAIL" && !clarifyActive && (
        <Button
          onClick={startClarify}
          disabled={clarifyLoading || loading || !input}
        >
          {clarifyLoading ? "Thinking..." : "Ask me clarifying questions"}
        </Button>
      )}

      {showClarifyPanel && (
        <Card>
          <CardContent>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-600">
                Clarifying — round {Math.min(clarifyRound || 1, maxRounds)} of{" "}
                {maxRounds}
                {clarifyTrack && clarifyTrack !== "unknown"
                  ? ` · ${clarifyTrack}`
                  : ""}
              </span>
              <button
                type="button"
                className="text-xs text-gray-400 hover:text-gray-600 underline"
                onClick={resetClarify}
              >
                reset
              </button>
            </div>

            {!clarifyReady && currentQuestions.length > 0 && (
              <div className="space-y-3">
                {currentQuestions.map((q) => (
                  <div key={q.id}>
                    <label className="block font-medium text-sm mb-1">
                      {q.text}
                    </label>
                    {q.options && q.options.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-1">
                        {q.options.map((opt) => (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => handleAnswerChange(q.id, opt)}
                            className={`text-xs px-2 py-1 rounded border ${
                              currentAnswers[q.id] === opt
                                ? "bg-blue-600 text-white border-blue-600"
                                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                    )}
                    <Input
                      value={currentAnswers[q.id] || ""}
                      onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                      placeholder={q.hint || "Your answer..."}
                    />
                  </div>
                ))}
                {clarifyReason && (
                  <p className="text-xs text-gray-500">{clarifyReason}</p>
                )}
              </div>
            )}

            {clarifyReady && (
              <div className="text-sm text-green-700">
                ✓ {clarifyReason || "I have enough — ready to optimize."}
              </div>
            )}

            <div className="flex flex-wrap gap-2 mt-3">
              {!clarifyReady &&
                currentQuestions.length > 0 &&
                clarifyRound < maxRounds && (
                  <Button
                    onClick={continueClarify}
                    disabled={clarifyLoading || loading}
                  >
                    {clarifyLoading ? "Thinking..." : "Continue"}
                  </Button>
                )}
              <Button
                onClick={() => optimizePrompt()}
                disabled={loading || clarifyLoading}
              >
                {loading ? "Optimizing..." : "Optimize now"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {!showClarifyPanel && (
        <Button onClick={() => optimizePrompt()} disabled={loading || !input}>
          {loading ? "Optimizing..." : "Optimize Prompt"}
        </Button>
      )}

      {optimizedPrompt && (
        <Card>
          <CardContent>
            <div className="whitespace-pre-wrap bg-gray-50 text-sm">
              {optimizedPrompt}
            </div>
            <Button
              onClick={() => handleCopy(optimizedPrompt)}
              className="mt-2"
            >
              Copy Prompt
            </Button>
          </CardContent>
        </Card>
      )}
 <StatsDashboard />
      {history.length > 0 && (
        <Card>
          <CardContent>
            <h2 className="text-lg font-bold mb-2">Prompt History</h2>
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {history.map((entry, idx) => (
                <div key={idx} className="border-b pb-2">
                  <p
                    className="text-xs text-gray-500 cursor-pointer"
                    onClick={() =>
                      setExpandedIdx(idx === expandedIdx ? null : idx)
                    }
                  >
                    [{entry.mode}] Target: {entry.targetAI} — Click to{" "}
                    {expandedIdx === idx ? "collapse" : "expand"}
                  </p>
                  {expandedIdx === idx && (
                    <div>
                      <Textarea
                        value={entry.raw}
                        onChange={(e) => {
                          const updated = [...history];
                          updated[idx].raw = e.target.value;
                          setHistory(updated);
                        }}
                        rows={3}
                      />
                      <p className="text-sm whitespace-pre-wrap mt-1 bg-gray-100 p-2 rounded">
                        {entry.optimized}
                      </p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        <Button onClick={() => handleCopy(entry.optimized)}>
                          Copy Optimized Prompt
                        </Button>
                        <Button onClick={() => optimizePrompt(entry.raw)}>
                          Re-optimize
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
